import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getHosts,
  addHost,
  updateHost,
  deleteHost,
  testHost,
  getHostStatistics,
  provisionHost,
  getJoinPackagePreview,
  getProvisionStatus,
  adoptProvisionedInstall,
} from "../api/hostApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { Hero } from "../components/adminSurface/AdminSurface";
import { ServerArt } from "../components/adminSurface/heroArt";

export default function HostManagement() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [hosts, setHosts] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAddHost, setShowAddHost] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const [formData, setFormData] = useState({
    hostName: "",
    displayName: "",
    address: "",
    sshPort: 22,
    sshUsername: "",
    sshKeyPath: "",
    isLocal: false,
    maxWorkers: 10,
    cpuCores: 8,
    memoryGB: 16,
    platform: "",
    serviceInstallPath: "",
    sshStrictHostKey: true,
  });
  const [testingHost, setTestingHost] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Installing Depository on a bare host: the conf the operator supplies, and the live log of the
  // installer running on the far side.
  const [installHost, setInstallHost] = useState(null);   // the host being installed
  const [installNodeName, setInstallNodeName] = useState("");
  const [installDir, setInstallDir] = useState("");
  const [installPreview, setInstallPreview] = useState(null); // { preview, warnings } or { error }
  const [installLoadingPreview, setInstallLoadingPreview] = useState(false);
  // Pasting a conf is the escape hatch, not the normal path: the server can describe its own
  // installation, and pasting would route the database password through the browser to say so.
  const [installUseOwnConf, setInstallUseOwnConf] = useState(false);
  // "node" installs the whole product on the target; "ingest" installs only the
  // worker and the gatherer, for a machine that sits next to the documents.
  const [installKind, setInstallKind] = useState("node");
  const [installConf, setInstallConf] = useState("");
  const [installLines, setInstallLines] = useState([]);
  const [installRunning, setInstallRunning] = useState(false);
  const [installResult, setInstallResult] = useState(null); // { success, message }
  const [installError, setInstallError] = useState("");
  const installIndexRef = useRef(0);
  const installPollRef = useRef(null);
  const installLogEndRef = useRef(null);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [hostsData, statsData] = await Promise.all([
        getHosts(token),
        getHostStatistics(token),
      ]);
      setHosts(hostsData);
      setStatistics(statsData);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const resetForm = () => {
    setFormData({
      hostName: "",
      displayName: "",
      address: "",
      sshPort: 22,
      sshUsername: "",
      sshKeyPath: "",
      isLocal: false,
      maxWorkers: 10,
      cpuCores: 8,
      memoryGB: 16,
      platform: "",
      serviceInstallPath: "",
      sshStrictHostKey: true,
    });
    setEditingHost(null);
    setShowAddHost(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionInProgress(true);
    setError("");
    try {
      if (editingHost) {
        await updateHost(token, editingHost.id, formData);
      } else {
        await addHost(token, formData);
      }
      resetForm();
      await fetchData();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleEdit = (host) => {
    setFormData({
      hostName: host.hostName,
      displayName: host.displayName || "",
      address: host.address,
      sshPort: host.sshPort,
      sshUsername: host.sshUsername || "",
      sshKeyPath: host.sshKeyPath || "",
      isLocal: host.isLocal,
      maxWorkers: host.maxWorkers,
      cpuCores: host.cpuCores,
      memoryGB: host.memoryGB,
      platform: host.platform || "",
      serviceInstallPath: host.serviceInstallPath || "",
      // A host saved before this field existed reads back as false; that is its real setting, not a
      // missing value, so it must not be coerced to the new-host default of true.
      sshStrictHostKey: host.sshStrictHostKey === true,
    });
    setEditingHost(host);
    setShowAddHost(true);
  };

  const handleDelete = async (id, hostName) => {
    if (!window.confirm(t("host.confirmDelete", { hostName }))) return;
    setActionInProgress(true);
    setError("");
    try {
      await deleteHost(token, id);
      await fetchData();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const openInstall = (host) => {
    setInstallHost(host);
    setInstallConf("");
    setInstallUseOwnConf(false);
    setInstallNodeName(host.hostName || "");
    setInstallKind("node");
    setInstallDir(host.serviceInstallPath || "");
    setInstallPreview(null);
    setInstallLines([]);
    setInstallResult(null);
    setInstallError("");
    setInstallRunning(false);
    installIndexRef.current = 0;
  };

  const loadPreview = async (host, nodeName, dir) => {
    if (!host) return;
    setInstallLoadingPreview(true);
    try {
      const res = await getJoinPackagePreview(token, host.id, {
        nodeName,
        installDir: dir || undefined,
        kind: installKind,
      });
      setInstallPreview(res);
    } catch (err) {
      setInstallPreview({ ok: false, error: friendlyMessageForApiError(err) });
    } finally {
      setInstallLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (!installHost || installUseOwnConf || installRunning || installResult) return;
    const id = setTimeout(() => loadPreview(installHost, installNodeName, installDir), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installHost, installNodeName, installDir, installUseOwnConf, installKind]);

  const closeInstall = () => {
    if (installPollRef.current) {
      clearInterval(installPollRef.current);
      installPollRef.current = null;
    }
    setInstallHost(null);
    // The conf holds the database password and the JWT secret; do not leave it sitting in component
    // state after the dialog is dismissed.
    setInstallConf("");
  };

  // Polling rather than a socket: an install runs for minutes and only appends lines, so asking for
  // "what is new since line N" every couple of seconds is both simpler and cheaper than holding a
  // connection open for the whole run.
  const pollInstall = async (hostId) => {
    try {
      const status = await getProvisionStatus(token, hostId, installIndexRef.current);
      if (!status?.found) return;
      if (status.lines?.length) {
        installIndexRef.current = status.nextIndex;
        setInstallLines((prev) => [...prev, ...status.lines]);
      }
      if (status.completed) {
        if (installPollRef.current) {
          clearInterval(installPollRef.current);
          installPollRef.current = null;
        }
        setInstallRunning(false);
        setInstallResult({ success: status.success, message: status.message });
        if (status.success) await fetchData();
      }
    } catch (err) {
      setInstallError(friendlyMessageForApiError(err));
    }
  };

  const startInstall = async () => {
    if (!installHost) return;
    setInstallError("");
    setInstallResult(null);
    setInstallLines([]);
    installIndexRef.current = 0;
    try {
      await provisionHost(
        token,
        installHost.id,
        installUseOwnConf
          ? { conf: installConf }
          : {
              nodeName: installNodeName || installHost.hostName,
              installDir: installDir || undefined,
              kind: installKind,
            }
      );
      setInstallRunning(true);
      // Clear the pasted conf as soon as the server has it — it is a credential, and it has no further
      // use in the browser.
      setInstallConf("");
      await pollInstall(installHost.id);
      installPollRef.current = setInterval(() => pollInstall(installHost.id), 2000);
    } catch (err) {
      setInstallError(friendlyMessageForApiError(err));
    }
  };

  const adoptInstall = async () => {
    if (!installHost) return;
    try {
      const res = await adoptProvisionedInstall(token, installHost.id);
      await fetchData();
      setInstallResult((prev) => ({ ...prev, message: res?.message || prev?.message }));
    } catch (err) {
      setInstallError(friendlyMessageForApiError(err));
    }
  };

  useEffect(() => {
    return () => {
      if (installPollRef.current) clearInterval(installPollRef.current);
    };
  }, []);

  useEffect(() => {
    installLogEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [installLines]);

  const handleTest = async (id, hostName) => {
    setTestingHost(id);
    setError("");
    try {
      const result = await testHost(token, id);
      alert(
        result.isSuccessful
          ? t("host.testSuccess", { hostName })
          : t("host.testFailed", { hostName, message: result.message })
      );
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setTestingHost(null);
    }
  };

  const getWorkerCount = (hostName) => {
    const stat = statistics.find((s) => s.hostName === hostName);
    return stat ? stat.workerCount : 0;
  };

  const statusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "healthy") return "hm-badge hm-badge--success";
    if (s === "unhealthy") return "hm-badge hm-badge--danger";
    return "hm-badge hm-badge--neutral";
  };

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />

      <div className="page-container hm">
        {/* Header */}
        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <rect x="2" y="3" width="20" height="8" rx="2" />
              <rect x="2" y="13" width="20" height="8" rx="2" />
              <line x1="6" y1="7" x2="6.01" y2="7" />
              <line x1="6" y1="17" x2="6.01" y2="17" />
            </svg>
          }
          title={t("host.management")}
          subtitle={t("host.subtitle")}
          logo={<ServerArt />}
          actions={
            <button
              className="asf-hero__btn"
              onClick={() => (showAddHost ? resetForm() : setShowAddHost(true))}
              disabled={actionInProgress}
            >
              {showAddHost
                ? t("host.cancelButton", { defaultValue: "Cancel" })
                : t("host.addButton", { defaultValue: "+ Add Host" })}
            </button>
          }
        />

        {/* Error */}
        {error && <div className="hm-alert hm-alert--error">{error}</div>}

        {/* Form */}
        {showAddHost && (
          <div className="hm-card hm-form-card">
            <h2 className="hm-card__title">
              {editingHost
                ? t("host.editTitle", { defaultValue: "Edit Host Configuration" })
                : t("host.addTitle", { defaultValue: "Add New Host" })}
            </h2>
            <p className="hm-card__desc">
              {editingHost ? t("host.editSubtitle") : t("host.addSubtitle")}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="hm-form-grid hm-form-grid--2">
                <div className="hm-field">
                  <label className="hm-label">
                    {t("host.hostName")} <span className="hm-req">*</span>
                  </label>
                  <input
                    className="hm-input"
                    type="text"
                    required
                    value={formData.hostName}
                    onChange={(e) => setFormData({ ...formData, hostName: e.target.value })}
                    disabled={editingHost !== null}
                    placeholder="worker-node-01"
                  />
                </div>
                <div className="hm-field">
                  <label className="hm-label">{t("host.displayName")}</label>
                  <input
                    className="hm-input"
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="Production Worker Node 1"
                  />
                </div>
                <div className="hm-field">
                  <label className="hm-label">
                    {t("host.address")} <span className="hm-req">*</span>
                  </label>
                  <input
                    className="hm-input"
                    type="text"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="192.168.1.100 or example.com"
                  />
                </div>
                <div className="hm-field">
                  <label className="hm-label hm-label--check">
                    <input
                      type="checkbox"
                      className="hm-checkbox"
                      checked={formData.isLocal}
                      onChange={(e) => setFormData({ ...formData, isLocal: e.target.checked })}
                    />
                    {t("host.localHost")}
                  </label>
                  <p className="hm-help">{t("host.localHostHelp")}</p>
                </div>
              </div>

              {!formData.isLocal && (
                <div className="hm-form-grid hm-form-grid--3">
                  <div className="hm-field">
                    <label className="hm-label">{t("host.sshPort")}</label>
                    <input
                      className="hm-input"
                      type="number"
                      value={formData.sshPort}
                      onChange={(e) => setFormData({ ...formData, sshPort: parseInt(e.target.value) || 22 })}
                    />
                  </div>
                  <div className="hm-field">
                    <label className="hm-label">{t("host.sshUsername")}</label>
                    <input
                      className="hm-input"
                      type="text"
                      value={formData.sshUsername}
                      onChange={(e) => setFormData({ ...formData, sshUsername: e.target.value })}
                      placeholder="ubuntu"
                    />
                  </div>
                  <div className="hm-field">
                    <label className="hm-label">{t("host.sshKeyPath")}</label>
                    <input
                      className="hm-input"
                      type="text"
                      value={formData.sshKeyPath}
                      onChange={(e) => setFormData({ ...formData, sshKeyPath: e.target.value })}
                      placeholder="~/.ssh/id_rsa"
                    />
                  </div>
                </div>
              )}

              {!formData.isLocal && (
                <div className="hm-field" style={{ marginBottom: 18 }}>
                  <label className="hm-label hm-label--check">
                    <input
                      type="checkbox"
                      className="hm-checkbox"
                      checked={formData.sshStrictHostKey}
                      onChange={(e) => setFormData({ ...formData, sshStrictHostKey: e.target.checked })}
                    />
                    {t("host.strictHostKey")}
                  </label>
                  <p className="hm-help">{t("host.strictHostKeyHelp")}</p>
                </div>
              )}

              {/* Native-service deployment: what a service needs and a container does not. */}
              <div className="hm-form-grid hm-form-grid--2">
                <div className="hm-field">
                  <label className="hm-label">{t("host.platform")}</label>
                  <select
                    className="hm-input"
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  >
                    <option value="">{t("host.platformAuto")}</option>
                    <option value="Linux">{t("host.platformLinux")}</option>
                    <option value="Windows">{t("host.platformWindows")}</option>
                  </select>
                  <p className="hm-help">{t("host.platformHelp")}</p>
                </div>
                <div className="hm-field">
                  <label className="hm-label">{t("host.serviceInstallPath")}</label>
                  <input
                    className="hm-input"
                    type="text"
                    value={formData.serviceInstallPath}
                    onChange={(e) => setFormData({ ...formData, serviceInstallPath: e.target.value })}
                    placeholder="/opt/depository"
                  />
                  <p className="hm-help">{t("host.serviceInstallPathHelp")}</p>
                </div>
              </div>

              <div className="hm-form-grid hm-form-grid--3">
                <div className="hm-field">
                  <label className="hm-label">{t("host.maxWorkers")}</label>
                  <input
                    className="hm-input"
                    type="number"
                    value={formData.maxWorkers}
                    onChange={(e) => setFormData({ ...formData, maxWorkers: parseInt(e.target.value) || 1 })}
                    min="1"
                  />
                </div>
                <div className="hm-field">
                  <label className="hm-label">{t("host.cpuCores")}</label>
                  <input
                    className="hm-input"
                    type="number"
                    value={formData.cpuCores}
                    onChange={(e) => setFormData({ ...formData, cpuCores: parseInt(e.target.value) || 1 })}
                    min="1"
                  />
                </div>
                <div className="hm-field">
                  <label className="hm-label">{t("host.memoryGb")}</label>
                  <input
                    className="hm-input"
                    type="number"
                    value={formData.memoryGB}
                    onChange={(e) => setFormData({ ...formData, memoryGB: parseInt(e.target.value) || 1 })}
                    min="1"
                  />
                </div>
              </div>

              <div className="hm-form-actions">
                <button type="submit" className="adm-btn adm-btn--primary" disabled={actionInProgress}>
                  {actionInProgress
                    ? t("host.saving", { defaultValue: "Saving..." })
                    : editingHost
                    ? t("host.updateButton", { defaultValue: "Update Host" })
                    : t("host.addHostButton", { defaultValue: "Add Host" })}
                </button>
                <button type="button" className="adm-btn adm-btn--outline" onClick={resetForm} disabled={actionInProgress}>
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="hm-empty">
            <p className="hm-empty__text">{t("host.loadingHosts")}</p>
          </div>
        ) : hosts.length === 0 && !showAddHost ? (
          <div className="hm-empty hm-empty--dashed">
            <h3 className="hm-empty__title">{t("host.noHosts")}</h3>
            <p className="hm-empty__text">{t("host.noHostsSubtitle")}</p>
            <button className="adm-btn adm-btn--primary" onClick={() => setShowAddHost(true)}>
              {t("host.addFirstHost")}
            </button>
          </div>
        ) : (
          <div className="hm-host-list">
            {hosts.map((host) => (
              <div key={host.id} className={`hm-card hm-host-card${host.isLocal ? " hm-host-card--local" : ""}`}>
                <div className="hm-host-card__top">
                  <div className="hm-host-card__info">
                    <div className="hm-host-card__name-row">
                      <h3 className="hm-host-card__name">
                        {host.displayName || host.hostName}
                      </h3>
                      {host.isLocal && (
                        <span className="hm-badge hm-badge--info">{t("worker.localBadge")}</span>
                      )}
                      <span className={statusClass(host.healthStatus)}>
                        {host.healthStatus || "Unknown"}
                      </span>
                    </div>

                    <div className="hm-host-card__meta">
                      <div className="hm-host-card__meta-item">
                        <span className="hm-host-card__meta-label">{t("host.addressLabel")}</span>
                        <span className="hm-host-card__meta-value">{host.address}</span>
                      </div>
                      {!host.isLocal && (
                        <div className="hm-host-card__meta-item">
                          <span className="hm-host-card__meta-label">{t("host.sshLabel")}</span>
                          <span className="hm-host-card__meta-value">
                            {host.sshUsername}@{host.address}:{host.sshPort}
                          </span>
                        </div>
                      )}
                      <div className="hm-host-card__meta-item">
                        <span className="hm-host-card__meta-label">{t("host.workersLabel")}</span>
                        <span className="hm-host-card__meta-value">
                          {getWorkerCount(host.hostName)} / {host.maxWorkers}
                        </span>
                      </div>
                      <div className="hm-host-card__meta-item">
                        <span className="hm-host-card__meta-label">{t("host.resourcesLabel")}</span>
                        <span className="hm-host-card__meta-value">
                          {host.cpuCores} {t("worker.cores")}, {host.memoryGB} {t("worker.gbRam")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="hm-host-card__actions">
                    <button
                      className="adm-btn adm-btn--outline"
                      onClick={() => handleTest(host.id, host.hostName)}
                      disabled={testingHost === host.id || actionInProgress}
                    >
                      {testingHost === host.id
                        ? t("host.testing", { defaultValue: "Testing..." })
                        : t("host.test", { defaultValue: "Test" })}
                    </button>
                    {!host.isLocal && (
                      <button
                        className="adm-btn adm-btn--outline"
                        onClick={() => openInstall(host)}
                        disabled={actionInProgress}
                        title={t("host.installHelp")}
                      >
                        {t("host.install")}
                      </button>
                    )}
                    <button
                      className="adm-btn adm-btn--outline"
                      onClick={() => handleEdit(host)}
                      disabled={actionInProgress}
                    >
                      {t("host.edit")}
                    </button>
                    <button
                      className="adm-btn adm-btn--danger-outline"
                      onClick={() => handleDelete(host.id, host.hostName)}
                      disabled={actionInProgress || getWorkerCount(host.hostName) > 0}
                      title={
                        getWorkerCount(host.hostName) > 0
                          ? t("host.cannotDelete")
                          : t("host.deleteHost")
                      }
                    >
                      {t("host.deleteHost")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {installHost && (
        <div className="dbModalOverlay" onClick={installRunning ? undefined : closeInstall}>
          <div
            className="dbModalCard"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 760, width: "92%", textAlign: "left" }}
          >
            <div className="dbModalTitle" style={{ textAlign: "left" }}>
              {t("host.installTitle", { hostName: installHost.hostName })}
            </div>
            <div className="dbModalDesc" style={{ textAlign: "left" }}>
              {t("host.installIntro")}
            </div>

            {!installRunning && !installResult && !installUseOwnConf && (
              <>
                <div className="hm-field" style={{ marginTop: 12 }}>
                  <label className="hm-label">{t("host.installKind")}</label>
                  <select
                    className="hm-input"
                    value={installKind}
                    onChange={(e) => setInstallKind(e.target.value)}
                  >
                    <option value="node">{t("host.installKindNode")}</option>
                    <option value="ingest">{t("host.installKindIngest")}</option>
                  </select>
                  <p className="hm-help">
                    {installKind === "ingest"
                      ? t("host.installKindIngestHelp")
                      : t("host.installKindNodeHelp")}
                  </p>
                </div>

                <div className="hm-form-grid hm-form-grid--2" style={{ marginTop: 12 }}>
                  <div className="hm-field">
                    <label className="hm-label">{t("host.installNodeName")}</label>
                    <input
                      className="hm-input"
                      type="text"
                      value={installNodeName}
                      onChange={(e) => setInstallNodeName(e.target.value)}
                      placeholder={installHost.hostName}
                    />
                    <p className="hm-help">{t("host.installNodeNameHelp")}</p>
                  </div>
                  <div className="hm-field">
                    <label className="hm-label">{t("host.serviceInstallPath")}</label>
                    <input
                      className="hm-input"
                      type="text"
                      value={installDir}
                      onChange={(e) => setInstallDir(e.target.value)}
                      placeholder="/opt/depository"
                    />
                    <p className="hm-help">{t("host.installDirHelp")}</p>
                  </div>
                </div>

                <label className="hm-label" style={{ marginTop: 8 }}>
                  {t("host.installPreviewLabel")}
                </label>
                {installLoadingPreview && !installPreview && (
                  <p className="hm-help">{t("host.installPreviewLoading")}</p>
                )}
                {installPreview && installPreview.ok === false && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#b91c1c",
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 6,
                      padding: "10px 12px",
                    }}
                  >
                    {installPreview.error}
                  </div>
                )}
                {installPreview?.ok && (
                  <>
                    <pre
                      style={{
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        padding: 12,
                        maxHeight: 220,
                        overflow: "auto",
                        fontSize: 12,
                        lineHeight: 1.5,
                        margin: 0,
                      }}
                    >
                      {installPreview.preview}
                    </pre>
                    <p className="hm-help">{t("host.installPreviewHelp")}</p>
                    {installPreview.warnings?.map((w, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 12,
                          color: "#92400e",
                          background: "#fffbeb",
                          border: "1px solid #fde68a",
                          borderRadius: 6,
                          padding: "8px 10px",
                          marginTop: 8,
                        }}
                      >
                        {w}
                      </div>
                    ))}
                  </>
                )}

                <button
                  type="button"
                  onClick={() => setInstallUseOwnConf(true)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginTop: 12,
                    color: "#2563eb",
                    cursor: "pointer",
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                >
                  {t("host.installUseOwnConf")}
                </button>
              </>
            )}

            {!installRunning && !installResult && installUseOwnConf && (
              <>
                <div
                  style={{
                    fontSize: 12,
                    color: "#92400e",
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: 6,
                    padding: "8px 10px",
                    margin: "12px 0",
                  }}
                >
                  {t("host.installSecretWarning")}
                </div>
                <label className="hm-label">{t("host.installConfLabel")}</label>
                <textarea
                  className="hm-input"
                  rows={10}
                  value={installConf}
                  spellCheck={false}
                  onChange={(e) => setInstallConf(e.target.value)}
                  placeholder={"DB_PROVIDER=SqlServer\nJWT_SECRET=...\nDATA_DIR=...\nINSTALL_DIR=/opt/depository\nAPI_PORT=5220"}
                  style={{ fontFamily: "monospace", fontSize: 12, width: "100%", resize: "vertical" }}
                />
                <p className="hm-help">{t("host.installConfHelp")}</p>
                <button
                  type="button"
                  onClick={() => { setInstallUseOwnConf(false); setInstallConf(""); }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginTop: 8,
                    color: "#2563eb",
                    cursor: "pointer",
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                >
                  {t("host.installUseGenerated")}
                </button>
              </>
            )}

            {(installRunning || installLines.length > 0) && (
              <div
                style={{
                  marginTop: 12,
                  background: "#0d1117",
                  color: "#e6edf3",
                  borderRadius: 6,
                  padding: 12,
                  maxHeight: 320,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                {installLines.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      color: /FAILED|ERROR|\[ERR\]/.test(line)
                        ? "#f85149"
                        : /WARN/.test(line)
                        ? "#ffa657"
                        : "#e6edf3",
                      wordBreak: "break-word",
                    }}
                  >
                    {line}
                  </div>
                ))}
                <div ref={installLogEndRef} />
              </div>
            )}

            {installError && (
              <div style={{ marginTop: 12, color: "#b91c1c", fontSize: 13 }}>{installError}</div>
            )}

            {installResult && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 13,
                  color: installResult.success ? "#065f46" : "#b91c1c",
                }}
              >
                {installResult.message}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button
                type="button"
                className="dbModalCancel"
                onClick={closeInstall}
                disabled={installRunning}
                title={installRunning ? t("host.installBusy") : undefined}
              >
                {installResult ? t("common.close", { defaultValue: "Close" }) : t("host.cancelButton")}
              </button>

              {installResult?.success && installHost && !installHost.serviceInstallPath && (
                <button type="button" className="adm-btn adm-btn--outline" onClick={adoptInstall}>
                  {t("host.installAdopt")}
                </button>
              )}

              {!installResult && (
                <button
                  type="button"
                  className="primary-btn"
                  onClick={startInstall}
                  disabled={
                    installRunning ||
                    (installUseOwnConf ? !installConf.trim() : !installPreview?.ok)
                  }
                >
                  {installRunning
                    ? t("host.installRunning")
                    : t("host.installStart")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
